import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { patientService } from '../services/dataSource';
import { CreatePatientData } from '../types/patient';
import { useAuth } from '../hooks/useAuth';
import { useOrganizationStore } from '../stores/organizationStore';
import { toast } from '../utils/toast';
import { PhoneInput, EmailInput, DateInput, GenderSelect, LanguageSelect, EmergencyContactInput, TagInput, type GenderValue } from '../components/forms';
import { EmergencyContact } from '../types/patient';

const CreatePatient: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { operationMode } = useOrganizationStore();
  const [loading, setLoading] = useState(false);
  const [validationState, setValidationState] = useState({
    email: true,
    phone: true,
    dateOfBirth: true,
    emergencyContacts: true, // optional (AMP-144); EmergencyContactInput reports it
  });
  const [formData, setFormData] = useState<CreatePatientData>({
    firstName: '',
    lastName: '',
    demographics: {
      dateOfBirth: '',
      gender: undefined,
      language: 'en'
    },
    contact: {
      phoneHash: '',
      emailHash: ''
    },
    emergencyContacts: [],
    medicalRecord: {
      mrn: '',
      allergies: [],
      conditions: [],
      medications: []
    }
  });

  // Only standalone admins and operators create patients; anyone else is sent back to the list.
  const blockedReason = operationMode === 'connected'
    ? 'Cannot create patients in connected mode'
    : user?.role === 'admin' || user?.role === 'operator'
    ? null
    : 'You do not have permission to create patients';

  useEffect(() => {
    if (blockedReason) {
      toast.error(blockedReason);
      navigate('/patients', { replace: true });
    }
  }, [blockedReason, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // Basic validation
      if (!formData.firstName || !formData.lastName) {
        toast.error('First name and last name are required');
        return;
      }

      if (!formData.demographics?.dateOfBirth) {
        toast.error('Date of birth is required');
        return;
      }

      if (!formData.demographics?.gender) {
        toast.error('Gender is required');
        return;
      }

      if (!formData.demographics?.language) {
        toast.error('Preferred language is required');
        return;
      }

      // Check form validation state
      const hasValidationErrors = !validationState.email || !validationState.phone || !validationState.dateOfBirth || !validationState.emergencyContacts;
      if (hasValidationErrors) {
        toast.error('Please fix validation errors before submitting');
        return;
      }

      // Create patient
      const newPatient = await patientService.createPatient(formData);
      
      toast.success('Patient created successfully');
      navigate(`/patients/${newPatient.id}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create patient';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (blockedReason) return null;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/patients')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Patients
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create New Patient</h1>
        <p className="mt-1 text-sm text-gray-600">
          Add a new patient to the system. All information is encrypted and secure.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient Information Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Patient Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="Enter first name"
                value={formData.firstName || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  firstName: e.target.value
                }))}
                required
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="Enter last name"
                value={formData.lastName || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  lastName: e.target.value
                }))}
                required
              />
            </div>
          </div>
        </div>

        {/* Demographics Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Demographics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateInput
              label="Date of Birth"
              value={formData.demographics?.dateOfBirth || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                demographics: {
                  ...prev.demographics!,
                  dateOfBirth: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, dateOfBirth: isValid }))}
              showAge={true}
              maxDate={new Date().toISOString().split('T')[0]} // Today's date
              required={true}
              isDateOnly={true}
            />

            <GenderSelect
              label="Gender"
              value={formData.demographics?.gender || ''}
              onChange={(value: GenderValue) => setFormData(prev => ({
                ...prev,
                demographics: {
                  ...prev.demographics!,
                  gender: value
                }
              }))}
              required={true}
              showIcons={true}
            />

            <LanguageSelect
              label="Preferred Language"
              value={formData.demographics?.language || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                demographics: {
                  ...prev.demographics!,
                  language: value
                }
              }))}
              placeholder="Select preferred language"
              className="md:col-span-2"
              required={true}
            />
          </div>
        </div>

        {/* Contact Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h2>
          <p className="text-sm text-gray-600 mb-4">
            Note: Contact information will be hashed for privacy protection
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhoneInput
              label="Phone Number"
              value={formData.contact?.phoneHash || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact!,
                  phoneHash: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, phone: isValid }))}
              placeholder="Enter phone number"
            />

            <EmailInput
              label="Email Address"
              value={formData.contact?.emailHash || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact!,
                  emailHash: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, email: isValid }))}
              placeholder="patient@example.com"
            />
          </div>
        </div>

        {/* Emergency Contacts Section */}
        <div className="card p-6">
          <EmergencyContactInput
            value={formData.emergencyContacts}
            onChange={(contacts: EmergencyContact[]) => setFormData(prev => ({
              ...prev,
              emergencyContacts: contacts
            }))}
            onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, emergencyContacts: isValid }))}
            required={false}
            minContacts={1}
            maxContacts={5}
          />
        </div>

        {/* Medical Record Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Medical Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Record Number (MRN)
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="MRN-12345"
                value={formData.medicalRecord?.mrn || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  medicalRecord: {
                    ...prev.medicalRecord!,
                    mrn: e.target.value
                  }
                }))}
              />
            </div>

            <TagInput
              label="Allergies"
              value={formData.medicalRecord?.allergies || []}
              onChange={(tags) => setFormData(prev => ({
                ...prev,
                medicalRecord: {
                  ...prev.medicalRecord!,
                  allergies: tags
                }
              }))}
              placeholder="Type allergy and press Enter"
              suggestions={[
                'Penicillin',
                'Aspirin',
                'Ibuprofen',
                'Shellfish',
                'Peanuts',
                'Tree nuts',
                'Eggs',
                'Milk',
                'Soy',
                'Wheat',
                'Fish',
                'Latex',
                'Sulfa drugs'
              ]}
              helperText="Common allergies will appear as suggestions"
              maxTags={50}
            />

            <TagInput
              label="Medical Conditions"
              value={formData.medicalRecord?.conditions || []}
              onChange={(tags) => setFormData(prev => ({
                ...prev,
                medicalRecord: {
                  ...prev.medicalRecord!,
                  conditions: tags
                }
              }))}
              placeholder="Type condition and press Enter"
              suggestions={[
                'Hypertension',
                'Diabetes Type 2',
                'Diabetes Type 1',
                'Asthma',
                'COPD',
                'Heart Disease',
                'Arthritis',
                'Osteoporosis',
                'Depression',
                'Anxiety',
                'Hypothyroidism',
                'Hyperthyroidism',
                'High Cholesterol'
              ]}
              helperText="Common conditions will appear as suggestions"
              maxTags={50}
            />

            <TagInput
              label="Current Medications"
              value={formData.medicalRecord?.medications || []}
              onChange={(tags) => setFormData(prev => ({
                ...prev,
                medicalRecord: {
                  ...prev.medicalRecord!,
                  medications: tags
                }
              }))}
              placeholder="Type medication and press Enter"
              suggestions={[
                'Metformin',
                'Lisinopril',
                'Atorvastatin',
                'Levothyroxine',
                'Amlodipine',
                'Metoprolol',
                'Omeprazole',
                'Simvastatin',
                'Losartan',
                'Gabapentin',
                'Hydrochlorothiazide',
                'Sertraline',
                'Aspirin',
                'Insulin'
              ]}
              helperText="Common medications will appear as suggestions"
              maxTags={100}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/patients')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Patient'}
          </button>
        </div>
      </form>

      {/* HIPAA Notice */}
      <div className="mt-6 card p-4 bg-blue-50 border border-blue-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              <strong>Security Notice:</strong> All patient data is encrypted at rest and in transit. 
              Contact information is hashed for additional privacy protection. This action will be logged for audit purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePatient;